import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DashStyleValue, SeriesOptionsType } from 'highcharts';
import { Card, Space, Typography } from 'antd';
import { useTheme } from '../../../../../../../providers/theme';
import { getColors } from '../../../../../../../components/utils/getColors';
import { useSelected } from '../../../../../../../providers/selectedContext';
import ChartSettings from '../../../../../../../components/chart/settings';
import { ChartSettingsProps } from '../../../../../../../@types/data/types';
import { TCorrelationValue } from '../../../../../types';
import { FZI_ROCK_TYPES, PORO_PERCENT_VALUES, generateRockTypeCurve } from './utils';
import { buildAnalysisTypeLegendSeries } from '../analysisTypeLegend';
import { Chart } from '../../../../../../../components/chart/BaseChart';
import { ChartSpec } from '../../../../../../../components/chart/chartSpec';
import { CHART_BOOST_ENABLED, EXPERIMENT_BOOST } from '../../../../../../../components/chart/boost';

const ROCK_TYPE_COLORS = [
  '#e41a1c', '#377eb8', '#4daf4a', '#984ea3',
  '#ff7f00', '#a65628', '#f781bf',
];

const makeRockTypeSeries = (label: string, fzi: number, color: string): SeriesOptionsType => ({
  id: `rt-${label}`,
  name: `${label} (FZI=${fzi})`,
  type: 'line',
  data: generateRockTypeCurve(fzi, PORO_PERCENT_VALUES),
  color,
  marker: { enabled: false },
  dashStyle: 'ShortDash' as DashStyleValue,
  enableMouseTracking: true,
  showInLegend: false,
  zIndex: 1,
  tooltip: {
    headerFormat: '',
    pointFormat: `<b>${label}</b> (FZI=${fzi})<br/>Porosity: {point.x:.1f}%<br/>K: {point.y:.2f} mD`
  }
});

interface PermPorosityChartProps {
  permData: TCorrelationValue[];
  containerWidth?: string | number;
  height?: string | number;
}

const PermPorosityChart: React.FC<PermPorosityChartProps> = ({ permData, containerWidth, height }) => {
  const { isDay } = useTheme();
  const colors = getColors(isDay);
  const { selectedInfo } = useSelected();
  const chartRef = useRef<any>(null);

  const chartZones = useMemo(() => {
    const zones = selectedInfo.project?.correlation?.zones;
    return zones?.map((zone: any, index: number) => ({
      ...zone,
      name: 'Rock Type ' + (index + 1)
    })) || [];
  }, [selectedInfo.project?.correlation?.zones]);

  const scatterSeries = useMemo((): SeriesOptionsType[] => {
    const validPoints = permData.filter(
      (p) => p.x != null && p.x > 0 && p.y != null && p.y > 0
    );

    const byZone = chartZones.map((zone: any) => {
      const pointsInZone = validPoints.filter(
        (p) => (p.xAxisValue ?? 0) >= zone.start && (p.xAxisValue ?? 0) <= zone.end
      );
      return {
        id: `perm-zone-${zone.name}`,
        name: zone.name,
        type: 'scatter' as const,
        data: pointsInZone.map((p) => ({ x: p.x * 100, y: p.y, name: p.name, marker: { symbol: p.marker?.symbol } })),
        color: zone.color ?? undefined,
        marker: { symbol: 'circle' },
        zIndex: 3,
        tooltip: {
          headerFormat: '<b>{series.name}</b><br/>',
          pointFormat: 'Sample: {point.name}<br/>Porosity: {point.x:.2f}%<br/>K: {point.y:.3f} mD'
        }
      } as SeriesOptionsType;
    });

    const outsideZone = validPoints.filter(
      (p) => !chartZones.some((z: any) => (p.xAxisValue ?? 0) >= z.start && (p.xAxisValue ?? 0) <= z.end)
    );

    byZone.push({
      id: 'perm-no-zone',
      name: 'No rock type',
      type: 'scatter' as const,
      data: outsideZone.map((p) => ({ x: p.x * 100, y: p.y, color: p.color ?? '#808080', name: p.name, marker: { symbol: p.marker?.symbol } })),
      color: '#808080',
      marker: { symbol: 'circle' },
      zIndex: 3,
      tooltip: {
        headerFormat: '<b>{series.name}</b><br/>',
        pointFormat: 'Sample: {point.name}<br/>Porosity: {point.x:.2f}%<br/>K: {point.y:.3f} mD'
      }
    } as SeriesOptionsType);

    return byZone;
  }, [permData, chartZones]);

  const rockTypeSeries = useMemo((): SeriesOptionsType[] =>
    FZI_ROCK_TYPES.map(({ label, fzi }, i) => makeRockTypeSeries(label, fzi, ROCK_TYPE_COLORS[i % ROCK_TYPE_COLORS.length])),
    []
  );

  const analysisLegendSeries = useMemo(
    () => buildAnalysisTypeLegendSeries(permData, colors.text),
    [permData, colors.text]
  );

  const autoDefaults = useMemo(() => {
    const validPoints = permData.filter((p) => p.x != null && p.x > 0 && p.y != null && p.y > 0);
    const xVals = validPoints.map((p) => p.x * 100).filter(Number.isFinite);
    const yVals = validPoints.map((p) => p.y).filter(Number.isFinite);
    return {
      xmin: xVals.length > 0 ? Math.min(...xVals) : 1.5,
      xmax: xVals.length > 0 ? Math.max(...xVals) : 46,
      ymin: yVals.length > 0 ? Math.min(...yVals) : 0.001,
      ymax: yVals.length > 0 ? Math.max(...yVals) : 10000,
    };
  }, [permData]);

  const [chartSettings, setChartSettings] = useState<ChartSettingsProps>({
    xmin: autoDefaults.xmin,
    xmax: autoDefaults.xmax,
    ymin: autoDefaults.ymin,
    ymax: autoDefaults.ymax,
  });
  const [userHasSetLimits, setUserHasSetLimits] = useState(false);

  useEffect(() => {
    if (!userHasSetLimits) setChartSettings(autoDefaults);
  }, [autoDefaults, userHasSetLimits]);

  const handleSetChartSettings = useCallback((s: ChartSettingsProps) => {
    setChartSettings(s);
    setUserHasSetLimits(true);
  }, []);

  const spec: ChartSpec = useMemo(() => {
    const safeYMin = (chartSettings.ymin != null && chartSettings.ymin > 0)
      ? chartSettings.ymin
      : (autoDefaults.ymin > 0 ? autoDefaults.ymin : 0.001);

    return {
      zoomType: 'xy',
      height,
      title: 'Permeability vs Porosity',
      xAxis: {
        title: 'Porosity (%)',
        min: chartSettings.xmin ?? autoDefaults.xmin,
        max: chartSettings.xmax ?? autoDefaults.xmax,
      },
      yAxis: {
        type: 'logarithmic',
        title: 'K_Klink (mD)',
        min: safeYMin,
        max: chartSettings.ymax ?? autoDefaults.ymax,
        minorTickInterval: 0.5,
        tickInterval: 1,
      },
      plotOptions: {
        scatter: {
          marker: {
            radius: 5,
            states: { hover: { enabled: true, lineColor: 'rgb(100,100,100)' } },
          },
        },
        line: {
          marker: { enabled: false },
        },
      },
      legend: true,
      boost: CHART_BOOST_ENABLED ? EXPERIMENT_BOOST : undefined,
      series: [...rockTypeSeries, ...scatterSeries, ...analysisLegendSeries],
    };
  }, [height, chartSettings, autoDefaults, rockTypeSeries, scatterSeries, analysisLegendSeries]);

  return (
    <Space
      direction="vertical"
      style={{
        backgroundColor: 'transparent',
        marginTop: '1rem',
        border: `1px solid ${colors.border}`,
        borderRadius: '16px',
        transition: 'width 0.5s',
        width: containerWidth ?? '49%',
        overflow: 'hidden',
      }}
    >
      <Card size="small" style={{ backgroundColor: 'transparent', border: 'none' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <Typography.Text strong style={{ color: colors.text }}>
            Permeability vs Porosity — Rock Type Curves
          </Typography.Text>
          <ChartSettings
            chartSettings={chartSettings}
            setChartSettings={handleSetChartSettings}
            autoDefaults={autoDefaults}
          />
        </div>
        <div style={{ position: 'relative', borderRadius: 12, border: `1px solid ${colors.border}`, padding: 12 }}>
          <Chart spec={spec} chartRef={chartRef} />
        </div>
      </Card>
    </Space>
  );
};

export default PermPorosityChart;
